import { Guideline, PageView } from "./types";

const API_BASE = (
  process.env.REACT_APP_OBJECT_HOST_URL || ""
).replace(/\/$/, "");

export function renderImagePath(imagePath: string | null | undefined): string | null {
  if (!imagePath) return null;
  const downloadUrl = `${API_BASE}/guidelines/storage/download?uri=${imagePath}`;
  if (imagePath.includes('img src="')) {
    return imagePath.replace(
      /img src="([^"]+)"/g,
      (_match, src) =>
        `img src="${API_BASE}/guidelines/storage/download?uri=${src}"`
    );
  }

  return downloadUrl;
}

export function sanitizeGuidelineText(text: string): string {
    if (!text) return "";
    
    // Remove hostname pollution from text
    const hostUrl = process.env.REACT_APP_OBJECT_HOST_URL || "";
    if (hostUrl && text.startsWith(hostUrl)) {
        return text.replace(hostUrl + "/", "");
    }
    
    return text;
}

export function groupChunksByPage(guidelines: Guideline[]): PageView[] {
    const pageMap = new Map<string, PageView>();
    
    guidelines.forEach((guideline) => {
        const pageNo = guideline.page_no ?? 0;
        const sectionId = guideline.section_id ?? `page-${pageNo}`;
        const key = `${sectionId}-${pageNo}`;
        
        if (!pageMap.has(key)) {
            pageMap.set(key, {
                page_no: pageNo,
                section_id: sectionId,
                section_text: sanitizeGuidelineText(guideline.section_text || guideline.text),
                chunks: [],
                metadata: guideline.metadata,
                source_img: guideline.source_img,
            });
        }
        
        const pageView = pageMap.get(key)!;
        if (guideline.chunk_id) {
            pageView.chunks.push({
                chunk_id: guideline.chunk_id,
                chunk_index: guideline.chunk_index ?? 0,
                text: sanitizeGuidelineText(guideline.text),
                short_title: guideline.short_title,
            });
        }
    });
    
    // Sort chunks within each page by chunk_index
    pageMap.forEach((pageView) => {
        pageView.chunks.sort((a, b) => a.chunk_index - b.chunk_index);
    });
    
    return Array.from(pageMap.values()).sort((a, b) => a.page_no - b.page_no);
}

/**
 * Highlights chunk text within the full section text using <mark> tags
 */
export function highlightChunksInText(
    sectionText: string,
    chunks: Array<{ text: string; chunk_id: string }>
): string {
    if (!sectionText || chunks.length === 0) return sectionText;
    
    let highlightedText = sectionText;
    const sanitizedSectionText = sanitizeGuidelineText(sectionText);
    
    // Sort chunks by length (longest first) to avoid partial matches
    const sortedChunks = [...chunks].sort((a, b) => b.text.length - a.text.length);
    
    sortedChunks.forEach((chunk) => {
        const sanitizedChunkText = sanitizeGuidelineText(chunk.text);
        if (!sanitizedChunkText) return;
        
        // Find chunk text in section text (case-insensitive)
        const index = sanitizedSectionText.toLowerCase().indexOf(sanitizedChunkText.toLowerCase());
        
        if (index !== -1) {
            const originalText = sanitizedSectionText.substring(index, index + sanitizedChunkText.length);
            const highlightedChunk = `<mark data-chunk-id="${chunk.chunk_id}">${originalText}</mark>`;
            
            // Replace first occurrence only
            highlightedText = highlightedText.replace(originalText, highlightedChunk);
        }
    });
    
    return highlightedText;
}
